import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, Button, Press, Loading, EmptyState } from "../ui";
import { Icon } from "../Icon";
import { useI18n } from "../i18n";
import { apiAdminResults } from "../api";
import { LESSONS } from "../content";
import { readinessOf } from "../adminUtil";

function Metric({ v, l }) {
  return (
    <View style={{ alignItems: "center", flex: 1 }}>
      <Text style={{ fontWeight: fonts.bold, fontSize: 15, color: colors.onSurface }}>{v}</Text>
      <Text numberOfLines={1} style={{ fontWeight: fonts.regular, fontSize: 10, color: colors.muted }}>{l}</Text>
    </View>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); apiAdminResults().then((d) => setData(d)).catch(() => setData([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const refresh = (
    <Press testID="admin-refresh" onClick={load} style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: colors.surfaceSecondary, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icon name="shuffle" size={18} color={colors.onSurface} weight="bold" />
    </Press>
  );

  return (
    <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surfaceSecondary }}>
      <View row style={{ padding: "20px 20px 12px", backgroundColor: colors.surface, borderBottom: `1px solid ${colors.divider}`, alignItems: "center", position: "sticky", top: 0, zIndex: 20 }}>
        <Press testID="admin-back" onClick={() => navigate(-1)} style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: colors.surfaceSecondary, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 10 }}>
          <Icon name="arrow-left" size={20} color={colors.onSurface} weight="bold" />
        </Press>
        <View style={{ flex: 1 }}>
          <Text style={{ fontWeight: fonts.bold, fontSize: 22, color: colors.onSurface }}>{tr("adminPanel")}</Text>
          <Text style={{ fontWeight: fonts.regular, fontSize: 13, color: colors.muted }}>{tr("results")}</Text>
        </View>
        {refresh}
      </View>
      {loading ? <Loading /> : (
        <View style={{ padding: 20 }}>
          <View style={{ marginBottom: 16 }}>
            <Button testID="admin-users-btn" title={tr("createUser")} icon="user" onPress={() => navigate("/admin/users")} />
          </View>
          {!data || data.length === 0 ? (
            <EmptyState icon="users" title={tr("noUsers")} />
          ) : (
            data.map((row) => {
              const r = readinessOf(row.data);
              const answered = row.data?.answered || 0;
              const acc = answered ? Math.round(((row.data?.correct || 0) / answered) * 100) : 0;
              const lessonsDone = Object.values(row.data?.lessons || {}).filter((l) => l.completed).length;
              const exams = (row.data?.exams || []).length;
              return (
                <Press key={row.user.id} testID={`admin-learner-${row.user.id}`} onClick={() => navigate(`/admin/learner/${row.user.id}`)}>
                  <Card style={{ marginBottom: 12 }}>
                    <View row style={{ alignItems: "center", gap: 12, marginBottom: 12 }}>
                      <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.brandTertiary, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Text style={{ fontWeight: fonts.bold, fontSize: 16, color: colors.brandPrimary }}>{row.user.name.charAt(0).toUpperCase()}</Text>
                      </View>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={{ fontWeight: fonts.bold, fontSize: 16, color: colors.onSurface }}>{row.user.name}</Text>
                        <Text style={{ fontWeight: fonts.regular, fontSize: 12, color: colors.muted }}>{row.user.email}</Text>
                      </View>
                      <View style={{ alignItems: "center" }}>
                        <Text style={{ fontWeight: fonts.bold, fontSize: 20, color: r >= 75 ? colors.success : r >= 50 ? colors.warning : colors.error }}>{r}%</Text>
                        <Text style={{ fontWeight: fonts.regular, fontSize: 10, color: colors.muted }}>{tr("examReadiness")}</Text>
                      </View>
                      <Icon name="caret-right" size={18} color={colors.muted} />
                    </View>
                    <View row style={{ justifyContent: "space-between", backgroundColor: colors.surfaceSecondary, borderRadius: 12, padding: 12 }}>
                      <Metric v={answered} l={tr("questionsAnswered")} />
                      <Metric v={`${acc}%`} l={tr("accuracy")} />
                      <Metric v={`${lessonsDone}/${LESSONS.length}`} l={tr("lessons")} />
                      <Metric v={exams} l={tr("exams")} />
                    </View>
                    {row.updated_at && (
                      <Text style={{ fontWeight: fonts.regular, fontSize: 11, color: colors.muted, marginTop: 8 }}>
                        {lang === "de" ? "Zuletzt aktiv" : "Last active"}: {new Date(row.updated_at).toLocaleString(lang === "de" ? "de-CH" : "en-GB")}
                      </Text>
                    )}
                  </Card>
                </Press>
              );
            })
          )}
        </View>
      )}
    </View>
  );
}
